import DevStaticApi from "./web-apis/backend-clients/dev-static-api";
import NotImplementedApi from "./web-apis/backend-clients/not-implemented-api";
import DevLocalhostApi from "./web-apis/backend-clients/dev-localhost-api";
import CockpitApi from "./web-apis/backend-clients/cockpit-api";
import { SystemFileQuery } from "./store/text-editor-file";
import BackendApiInterface from "./web-apis/backend-api-interface";
import CloudApi from "./web-apis/backend-clients/cloud-api";

export enum LoginType { None, AzureLogin };

/** Deployment mode of this entire Configuration UI environment -- either cloud or local. */
export enum DeploymentMode {
    Local = 'local',
    Cloud = 'cloud'
}

export interface ConfigInterface {
    isInsideFrame: boolean;
    allowTextEditor: boolean;
    allowDashboard?: boolean;
    allowLicenseUpdate: boolean,
    showDebugInfoInUi?: boolean;
    /** TODO: handle showErrorsInUi better and in a consistent fashion. Currently it's always true and doesn't really cause trouble, but in the future it might. */
    showErrorsInUi?: boolean,
    loginType: LoginType,
    getBackendApi: () => BackendApiInterface;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const localCockpitConfig: ConfigInterface = {
    isInsideFrame: true,
    allowTextEditor: true,
    allowLicenseUpdate: true,
    loginType: LoginType.None,
    getBackendApi: () => new CockpitApi(),
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const cloudConfig: ConfigInterface = {
    isInsideFrame: false,
    allowTextEditor: false,
    allowLicenseUpdate: false,
    loginType: LoginType.AzureLogin,
    getBackendApi: () => new CloudApi(),
};

// this configuration is for development only!
// use it to test config ui with static data without backend
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const devStaticConfig: ConfigInterface = {
    isInsideFrame: false,
    allowTextEditor: false,
    allowLicenseUpdate: false,
    loginType: LoginType.None,
    getBackendApi: () => new DevStaticApi(),
};


// this configuration is for development only!
// use it to test config ui with a local mode (non-cockpit) backend
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const devLocalhostConfig: ConfigInterface = {
    isInsideFrame: false,
    allowTextEditor: false,
    allowLicenseUpdate: false,
    showDebugInfoInUi: true,
    loginType: LoginType.None,
    getBackendApi: () => new DevLocalhostApi(),
};

const defaultConfig = {
    showErrorsInUi: true,
    showDebugInfoInUi: false,
    allowDashboard: true,
};

export function getConfigClient(deploymentMode: DeploymentMode): ConfigInterface {
    switch (deploymentMode) {
        case DeploymentMode.Cloud:
            return { ...defaultConfig, ...cloudConfig };
        case DeploymentMode.Local:
            return { ...defaultConfig, ...localCockpitConfig };
        default:
            throw new Error(`No valid config interface for deployment mode '${deploymentMode}'`);
    }
}

export const getSystemFileQueries = (): SystemFileQuery[] => {

    // this environment tag can be used in system file queries to search for files specific to this particular
    // environment configuration (e.g. "clinical" or "nonclinical"). the value is read from manifest.json.
    const environmentTag = new CockpitApi().getEnvironmentTag();

    // SystemFileQuery constructor arguments: search path, allow writing, description shown in UI
    return [
        // customized: modelConfig/modelSelection (default files are inside container, so not accessible)
        new SystemFileQuery(`/etc/mvision/${environmentTag}/mvbackend/modelConfig.json`, true, 'Model customization. Consider using the Model Customization page instead of modifying this file directly.'),
        new SystemFileQuery(`/etc/mvision/${environmentTag}/mvbackend/modelSelection.json`, true, 'Model selection rules. Please see supplied documentation regarding adjusting model selection rules.'),

        // files containing MVision provided credentials
        new SystemFileQuery('/etc/mvision/private/sp.txt', true, 'MVision-provided credentials.'),
        new SystemFileQuery('/etc/mvision/private/key.txt', true, 'MVision-provided credentials.'),
        new SystemFileQuery('/etc/mvision/private/acr-access-id.txt', true, 'MVision-provided credentials.'),
        new SystemFileQuery('/etc/mvision/private/acr-access-key.txt', true, 'MVision-provided credentials.'),

        // daemon configs: for changing destination TPS, etc.
        new SystemFileQuery(`/srv/mvision/${environmentTag}/mvdaemon/options.json`, true, 'Daemon configuration.'),

        // details of the MVision containers
        new SystemFileQuery(`/srv/mvision/${environmentTag}/docker-compose.yml`, true, 'MVision container details.'),

        // linux config files: SOME files might be absent
        new SystemFileQuery('/etc/apt/sources.list', true, 'Linux configuration.'),
        new SystemFileQuery('/{lib,etc,run}/netplan/*.yaml', true, 'Network configuration.'),
        new SystemFileQuery(`/etc/cron.d/mvision-${environmentTag}-upgrades`, true, 'Unattended OS upgrades.'),
        new SystemFileQuery('/etc/apt/apt.conf.d/20auto-upgrades', true, 'Unattended OS upgrades.'),
        new SystemFileQuery('/etc/apt/apt.conf.d/50unattended-upgrades', true, 'Unattended OS upgrades.'),
        new SystemFileQuery('/etc/security/pwquality.conf', true, 'Linux configuration.'),
        new SystemFileQuery('/etc/profile.d/proxy.sh', true, 'Unattended OS upgrades.'),
        new SystemFileQuery('/etc/systemd/timesyncd.conf', true, 'NTP servers.'),
        new SystemFileQuery('/etc/systemd/timesyncd.conf.d/*.conf', true, 'NTP servers.'),
        new SystemFileQuery('/run/systemd/timesyncd.conf.d/*.conf', true, 'NTP servers.'),
        new SystemFileQuery('/usr/lib/systemd/timesyncd.conf.d/*.conf', true, 'NTP servers.'),
        new SystemFileQuery('/etc/docker/daemon.json', true, 'Reserved IP address pool.'),
    ];
}